/**
 * The axis cell's context menu, wired up: {@link computeAxisCellContextMenu} in
 * `store/logic/AxisCellContextMenuLogic` decides which options the menu lists, how they read and
 * which of them are hidden or disabled, and this module attaches each option's handler.
 *
 * The handlers are the only impure part of the menu — they open the rename modal, log analytics and
 * read the current user — which is why they live here in the UI layer rather than with the compute.
 *
 * {@link useAxisCellContextMenu} is what `TableAxisCell` renders from;
 * {@link getContextMenuOptions} is the same thing without the React plumbing, so the wiring
 * can be unit tested directly.
 */

import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {
  logColumnAnalytics,
  StructuredTableAnalyticsParams,
} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import type {InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {USER_CREATED_COLUMN_NAME_MAX_LENGTH} from 'notebook/tables/constants/TableColumnConstants';
import {SelectionRange, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
// Imported for its types only, so that this module and the component it serves do not depend on
// each other at runtime.
import type {TableAxisCellProps} from 'notebook/tables/core-ui/TableAxisCell';
import {useAxisCellModel} from 'notebook/tables/store/hooks/useAxisCellModel';
import {
  AxisCellContextMenuInput,
  AxisCellMenuEntry,
  AxisCellMenuItem,
  AxisCellMenuItemEntry,
  computeAxisCellContextMenu,
  InsertDimDirection,
} from 'notebook/tables/store/logic/AxisCellContextMenuLogic';
import {AxisCellModel, isColumnHeaderModel} from 'notebook/tables/store/models/AxisCellModel';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import EntityStore from 'stores/EntityStore';
import * as modals from 'util/Modals';
import {ElementMouseEvent} from 'util/Types';

type MenuOption =
  | '-' // specifically, a menu separator
  | {
      name: string;
      click: (e: ElementMouseEvent) => void;
      hide: () => boolean;
      disabled?: boolean;
    };

export interface AnalyticsParams {
  fileAnalyticsParams: FileAnalyticsParams | null;
  structuredTableAnalyticsParams: StructuredTableAnalyticsParams | null;
}

/**
 * When ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION is enabled, tenant admins can lock and unlock columns
 * via the context menu even in execution context (e.g. entries), where lock/unlock is normally hidden.
 */
export function canModifyColumnLockAsAdmin(): boolean {
  return (
    BenchlingConfig.ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION &&
    Boolean(EntityStore.currentUser()?.isTenantAdmin)
  );
}

export function hasVisibleContextOptions(
  isGhostCell: boolean,
  contextMenuOptions: Array<contextmenu.ContextMenuOption>
): boolean {
  // Ghost cells in plain tables should never show context options/menus.
  if (isGhostCell) {
    return false;
  }

  function filterFunc(option: string | MenuOption | contextmenu.ClickableContextMenuOption | null): boolean {
    return typeof option === 'object' && !_.result(option, 'hide');
  }

  return contextMenuOptions.some((option) => filterFunc(option));
}

/** The action each of the menu's built-in options performs when chosen. */
interface AxisCellMenuHandlers {
  renameDim: () => void;
  toggleHeader: () => void;
  deleteTable: () => void;
  deleteDim: (range: SelectionRange) => void;
  setColumnProtected: (isProtected: boolean) => void;
  freezeColumn: () => void;
  unfreezeColumn: () => void;
  insertDim: (
    direction: InsertDimDirection,
    range: SelectionRange,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
}

/** Which side of the selection the given insert option inserts on. */
function getInsertDirection(item: AxisCellMenuItem): InsertDimDirection {
  return item === AxisCellMenuItem.INSERT_DIM_BEFORE ? 'BEFORE' : 'AFTER';
}

function isInsertItem(item: AxisCellMenuItem): boolean {
  return item === AxisCellMenuItem.INSERT_DIM_BEFORE || item === AxisCellMenuItem.INSERT_DIM_AFTER;
}

/** What the menu's inputs are for the given cell, from its model and the table it is rendered in. */
function getContextMenuInput(
  props: TableAxisCellProps,
  axisCellModel: AxisCellModel
): AxisCellContextMenuInput {
  return {
    axisCellModel,
    availableActions: {
      canRenameDim: props.renameDim != null,
      canDeleteDim: props.delDim != null,
      canDeleteTable: props.deleteTable != null,
      canToggleHeader: props.toggleHeader != null,
      canInsertDimBefore: props.insertDimBefore != null,
      canInsertDimAfter: props.insertDimAfter != null,
      canFreezeColumn: props.freezeThroughColumn != null,
      canUnfreezeColumn: props.unfreezeFromColumn != null,
    },
    extraOptions: props.getExtraContextMenuOptions?.(axisCellModel.selectionRange) ?? [],
    isSingleInputHeader: Boolean(props.isSingleInputHeader),
    canModifyColumnLockAsAdmin: canModifyColumnLockAsAdmin(),
  };
}

/**
 * The thin wiring between the menu's built-in options and the dimension actions they perform. Each
 * action is still a prop or a table mutation; only the modal and analytics side effects live here.
 */
function getContextMenuHandlers(
  props: TableAxisCellProps,
  axisCellModel: AxisCellModel,
  analyticsParams?: AnalyticsParams | null
): AxisCellMenuHandlers {
  const {dim, index} = axisCellModel;
  const columnHeader = isColumnHeaderModel(axisCellModel) ? axisCellModel : null;
  const label = columnHeader?.label ?? null;
  const columnConstraint = columnHeader?.columnConstraint ?? null;

  return {
    renameDim: () => {
      const {getDisallowedColumnNames} = props;
      const disallowedColumnNames = getDisallowedColumnNames ? getDisallowedColumnNames() : [];
      const validate = (name: string): boolean =>
        name === label ||
        (!disallowedColumnNames.includes(name) && name.length <= USER_CREATED_COLUMN_NAME_MAX_LENGTH);

      // A column label can be reset if it is explicitly set.
      let canReset = columnConstraint ? columnConstraint.canResetLabel : null;
      if (canReset == null) {
        canReset = label != null;
      }

      modals.prompt('Rename column', label, validate, {canReset}).then((name) => {
        if (props.renameDim && name != null) {
          props.renameDim(index, name);
        }
      });
    },

    toggleHeader: () => {
      props.toggleHeader?.();
    },

    deleteTable: () => {
      props.deleteTable?.();
    },

    deleteDim: (range) => {
      // TODO(T7364): Check that delDim is defined since the props can change after the context
      // menu is opened.
      if (typeof props.delDim === 'function') {
        props.delDim(range.minIndex, range.maxIndex);
        if (dim === TableDimension.COLUMN && analyticsParams) {
          logColumnAnalytics({
            columnConstraint,
            analyticsEvent: ELN_ANALYTICS_ACTIONS.DELETE_COLUMN,
            structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
            fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
          });
        }
      }
    },

    setColumnProtected: (newIsProtected) => {
      const {setColumnConstraint} = props;
      if (!setColumnConstraint) {
        return;
      }

      if (columnConstraint?.structuredTableColumn) {
        setColumnConstraint(
          index,
          reactUpdate(columnConstraint, {
            structuredTableColumn: {$merge: {isProtected: newIsProtected}},
            $merge: {isProtected: newIsProtected},
          }),
          {}
        );
      } else {
        setColumnConstraint(
          index,
          reactUpdate(columnConstraint || {}, {$merge: {isProtected: newIsProtected}}),
          {}
        );
      }
      if (analyticsParams) {
        logColumnAnalytics({
          columnConstraint,
          analyticsEvent: ELN_ANALYTICS_ACTIONS.TOGGLED_COLUMN_LOCKING,
          structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
          fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
          additionalMetadata: {
            setting: newIsProtected ? 'lock' : 'unlock',
          },
        });
      }
    },

    freezeColumn: () => {
      props.freezeThroughColumn?.(index);
    },

    unfreezeColumn: () => {
      props.unfreezeFromColumn?.(index);
    },

    insertDim: (direction, range, columnInfo) => {
      // TODO(T7364): Check that the insert action is defined since the props can change after the
      // context menu is opened.
      const insert = direction === 'BEFORE' ? props.insertDimBefore : props.insertDimAfter;
      if (typeof insert === 'function') {
        insert(range.minIndex, range.maxIndex, columnInfo);
      }
    },
  };
}

function runMenuItem(entry: AxisCellMenuItemEntry, handlers: AxisCellMenuHandlers): void {
  switch (entry.item) {
    case AxisCellMenuItem.RENAME_DIM:
      handlers.renameDim();
      break;
    case AxisCellMenuItem.TOGGLE_HEADER:
      handlers.toggleHeader();
      break;
    case AxisCellMenuItem.DELETE_TABLE:
      handlers.deleteTable();
      break;
    case AxisCellMenuItem.DELETE_DIM:
      if (entry.range != null) {
        handlers.deleteDim(entry.range);
      }
      break;
    case AxisCellMenuItem.LOCK_COLUMN:
      handlers.setColumnProtected(true);
      break;
    case AxisCellMenuItem.UNLOCK_COLUMN:
      handlers.setColumnProtected(false);
      break;
    case AxisCellMenuItem.FREEZE_COLUMN:
      handlers.freezeColumn();
      break;
    case AxisCellMenuItem.UNFREEZE_COLUMN:
      handlers.unfreezeColumn();
      break;
    case AxisCellMenuItem.INSERT_DIM_BEFORE:
    case AxisCellMenuItem.INSERT_DIM_AFTER:
      if (entry.range != null) {
        handlers.insertDim(getInsertDirection(entry.item), entry.range, {});
      }
      break;
    default:
      assert(false, `Unexpected context menu item ${entry.item}`);
  }
}

function toContextMenuOption(
  entry: AxisCellMenuEntry,
  handlers: AxisCellMenuHandlers
): contextmenu.ContextMenuOption {
  if (entry.kind === 'separator') {
    return '-';
  }
  if (entry.kind === 'extra') {
    return entry.option;
  }
  return {
    name: entry.name,
    hide: entry.isHidden,
    disabled: entry.isDisabled,
    tooltip: entry.tooltip,
    ...(entry.dataTestAttrs != null && {dataTestAttrs: entry.dataTestAttrs}),
    ...(entry.isClickable && {
      click: () => {
        runMenuItem(entry, handlers);
      },
    }),
    ...(isInsertItem(entry.item) &&
      entry.isClickable && {
        menu:
          entry.insertOptions?.map((option) => ({
            name: option.name,
            dataTestAttrs: option.dataTestAttrs,
            click: () => {
              if (entry.range != null) {
                handlers.insertDim(getInsertDirection(entry.item), entry.range, option.columnInfo);
              }
            },
          })) ?? null,
      }),
  };
}

/**
 * Builds the axis cell's context menu: {@link computeAxisCellContextMenu} decides which options the
 * menu lists, how they read and which of them are hidden or disabled, and this attaches the handler
 * for each of them.
 *
 * {@link useAxisCellContextMenu} is what the rendered cell calls; this takes the cell's model and
 * analytics parameters directly so the wiring can be exercised without rendering.
 */
export function getContextMenuOptions(
  props: TableAxisCellProps,
  axisCellModel: AxisCellModel,
  analyticsParams?: AnalyticsParams | null
): Array<contextmenu.ContextMenuOption> {
  const entries = computeAxisCellContextMenu(getContextMenuInput(props, axisCellModel));
  const handlers = getContextMenuHandlers(props, axisCellModel, analyticsParams);
  return entries.map((entry) => toContextMenuOption(entry, handlers));
}

/**
 * The context menu of the axis cell this is rendered in: the options it lists, and whether it
 * offers a menu at all.
 */
export function useAxisCellContextMenu(props: TableAxisCellProps): {
  menuOptions: Array<contextmenu.ContextMenuOption>;
  hasContextOptions: boolean;
} {
  const axisCellModel = useAxisCellModel();
  const {rootFolderId: projectId, fileId, templateCollectionId} = EditorPropertiesContext.useContext();
  const {structuredTableKind, provenanceUuid} = StructuredTableContext.useContext();

  const analyticsParams: AnalyticsParams = {
    structuredTableAnalyticsParams:
      structuredTableKind != null
        ? {structuredTableKind, structuredTableProvenanceUuid: provenanceUuid!}
        : null,
    fileAnalyticsParams: {fileId, projectId, templateCollectionId},
  };

  const menuOptions = getContextMenuOptions(props, axisCellModel, analyticsParams);

  return {
    menuOptions,
    hasContextOptions: hasVisibleContextOptions(axisCellModel.isGhostCell, menuOptions),
  };
}
